import { useEffect, useRef, useState } from "react";
import { fetchTrip, type Fetched } from "./api.ts";
import { TripMap } from "./Map.tsx";
import { Sheet } from "./Sheet.tsx";
import { boardingLine, fmtTime, headline, isLive, photoKey, progress, tokenFromPath, updatedAgo, type View } from "./trip.ts";

const POLL_MS = 5000;

/** Cliente-20: the live trip a rider (or a delivery sender) shared, polled every 5 s. */
export function App() {
  const token = tokenFromPath(window.location.pathname);
  const [state, setState] = useState<Fetched | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!token) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    const ctrl = new AbortController();
    const tick = async () => {
      try {
        const r = await fetchTrip(token, ctrl.signal);
        if (stop) return;
        // Keep showing the last good view through a network blip.
        setState((prev) => (r.kind === "error" && prev?.kind === "ok" ? prev : r));
        const finished = r.kind === "ended" || r.kind === "not_found" || (r.kind === "ok" && !isLive(r.view) && r.view.status !== "awaiting_payment");
        if (!finished) timer = setTimeout(tick, document.hidden ? POLL_MS * 3 : POLL_MS);
      } catch {
        /* aborted */
      }
    };
    void tick();
    const clock = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      stop = true;
      ctrl.abort();
      clearTimeout(timer);
      clearInterval(clock);
    };
  }, [token]);

  if (!token || state?.kind === "not_found") return <Message title="Link inválido" body="Confira se o endereço foi copiado inteiro. Links de viagem compartilhada começam com v.ichegoubr.com." />;
  if (state?.kind === "ended") return <Message title="Compartilhamento encerrado" body="A viagem terminou ou quem compartilhou parou de compartilhar. Este link não mostra mais a localização." ended />;
  if (!state) return <Shell live={false}><div className="stage"><div className="map-area" /><Sheet label="Carregando" peek={<div className="skeleton" style={{ height: 120 }} />} /></div></Shell>;
  if (state.kind === "error") return <Message title="Não foi possível carregar" body="Verifique sua conexão. A página tenta de novo sozinha." />;
  return <Live v={state.view} now={now} />;
}

function Live({ v, now }: { v: View; now: number }) {
  const h = headline(v);
  const live = isLive(v);
  const pct = progress(v, now);
  const board = boardingLine(v);
  const delivery = v.kind === "delivery";
  const deviated = v.routeStatus === "deviated";
  return (
    <Shell live={live} delivery={delivery}>
      <div className="stage">
        <div className="map-area">
          <TripMap view={v} />
          <span className="glass chip-updated" aria-live="polite">{live ? `Atualizado ${updatedAgo(v.updatedAt, now)}` : v.status === "completed" ? "Viagem concluída" : "Viagem encerrada"}</span>
          {live && <a className="glass chip-sos" href="tel:190" aria-label="Ligar para a emergência, 190">190</a>}
        </div>
        <Sheet
          label={delivery ? "Envio" : "Viagem"}
          peek={
            <>
              <div className="headline">
                <span className="headline-text">
                  <span className="lead">{h.lead}</span>
                  <b className="title">{h.title}</b>
                </span>
                {h.eta && (
                  <span className="eta">
                    <span className="eta-value">{h.eta.value}</span>
                    <span className="eta-label">{h.eta.label}</span>
                  </span>
                )}
              </div>
              {pct > 0 && (
                <div role="progressbar" aria-label={delivery ? "Progresso do envio" : "Progresso da viagem"} aria-valuenow={Math.round(pct * 100)} aria-valuemin={0} aria-valuemax={100} className="progress">
                  <span style={{ width: `${pct * 100}%` }} />
                </div>
              )}
              {board && deviated && <span className="note note-warn">{board}</span>}
              {v.status === "completed" && <span className="note">{delivery ? "Entregue" : "Chegou"} às {fmtTime(v.updatedAt)}. A localização deixa de ser compartilhada em instantes.</span>}
              {v.driver && (
                <div className="driver">
                  <span className="avatar-wrap">
                    <DriverPhoto url={v.driver.photoUrl} name={v.driver.firstName} />
                    <span className="rating" aria-label={`Nota ${v.driver.rating.toLocaleString("pt-BR")}`}>★ {v.driver.rating.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}</span>
                  </span>
                  <span className="driver-text">
                    <DriverName label={v.driver.firstName}>
                      {v.driver.identityVerified && <VerifiedCheck name={v.driver.firstName} delivery={delivery} />}
                    </DriverName>
                    <span>{v.driver.vehicle.model} {v.driver.vehicle.color.toLowerCase()}</span>
                  </span>
                  <Plate plate={v.driver.vehicle.plate} />
                </div>
              )}
            </>
          }
        >
          {delivery && v.deliverySteps && <Steps steps={v.deliverySteps} />}
          {board && !deviated && <span className="note">{board}</span>}
          <a className="btn-emergency" href="tel:190">Emergência 190</a>
          <span className="foot">Este link expira quando a {delivery ? "entrega" : "viagem"} termina.</span>
        </Sheet>
      </div>
    </Shell>
  );
}

/**
 * The driver's photo, or their initials without one (or if it fails to load). Every poll brings the
 * same photo signed again: the one on screen stays until the photo changes or its link expires.
 */
function DriverPhoto({ url, name }: { url?: string; name: string }) {
  const [src, setSrc] = useState(url);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (url && (!src || photoKey(url) !== photoKey(src))) {
      setSrc(url);
      setFailed(false);
    }
  }, [url, src]);
  if (!src || failed) return <span className="avatar" aria-hidden="true">{initials(name)}</span>;
  return <img className="avatar avatar-photo" src={src} alt="" onError={() => (url && url !== src ? setSrc(url) : setFailed(true))} />;
}

/** The name in bold; its last word and the check after it never break apart. */
function DriverName({ label, children }: { label: string; children: React.ReactNode }) {
  const i = label.lastIndexOf(" ");
  return (
    <b>
      {label.slice(0, i + 1)}
      <span className="nowrap">{label.slice(i + 1)}{children}</span>
    </b>
  );
}

/** The blue check after the driver's name (approved prova de vida); tapping it explains it, like the apps. */
function VerifiedCheck({ name, delivery }: { name: string; delivery: boolean }) {
  const dialog = useRef<HTMLDialogElement>(null);
  return (
    <>
      <button type="button" className="verified" aria-label="Identidade verificada. Saiba mais" onClick={() => dialog.current?.showModal()}>
        <CheckBadge size={16} />
      </button>
      <dialog
        ref={dialog}
        className="trust-dialog"
        aria-labelledby="trust-title"
        // A tap on the backdrop (the dialog element itself, outside its content) closes it.
        onClick={(e) => e.target === e.currentTarget && e.currentTarget.close()}
      >
        <div className="trust-body">
          <CheckBadge size={64} />
          <h2 id="trust-title">Identidade verificada</h2>
          <p>{name} confirmou a identidade com uma prova de vida: um vídeo ao vivo que a equipe ichegou comparou com a foto da CNH.</p>
          <p className="trust-note">{delivery ? "Confira a placa e o modelo do veículo na entrega." : "Confira sempre a placa e o modelo do carro antes de entrar."}</p>
          <form method="dialog">
            <button className="btn-ok">Entendi</button>
          </form>
        </div>
      </dialog>
    </>
  );
}

function CheckBadge({ size }: { size: number }) {
  return (
    <svg className="check-badge" width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="12" fill="#2563EB" />
      <path d="M7 12.4l3.2 3.1L17 8.8" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Steps({ steps }: { steps: NonNullable<View["deliverySteps"]> }) {
  const current = steps.findLastIndex((s) => s.at);
  return (
    <ol className="steps" aria-label="Etapas do envio">
      {steps.map((s, i) => (
        <li key={s.id} className={i <= current ? "done" : undefined} aria-current={i === current ? "step" : undefined}>
          <span className="step-dot" aria-hidden="true" />
          <span className="step-title">{s.title}</span>
          <span className="step-time">{s.at ? fmtTime(s.at) : ""}</span>
        </li>
      ))}
    </ol>
  );
}

/** Mercosul plate: blue band over the characters. */
function Plate({ plate }: { plate: string }) {
  return (
    <span className="plate" role="img" aria-label={`Placa ${plate}`}>
      <span className="plate-band" />
      <span className="plate-text">{plate}</span>
    </span>
  );
}

function Shell({ live, delivery, children }: { live: boolean; delivery?: boolean; children: React.ReactNode }) {
  return (
    <div className="page">
      <header className="top">
        <span className="brand"><img className="brand-logo" src="/logo.png" alt="ichegou" /><span className="brand-sub">{delivery ? "envio compartilhado" : "viagem compartilhada"}</span></span>
        {live && <span className="live"><span className="live-dot" />AO VIVO</span>}
      </header>
      {children}
    </div>
  );
}

function Message({ title, body, ended }: { title: string; body: string; ended?: boolean }) {
  return (
    <Shell live={false}>
      <section className="message">
        <span className={`message-icon${ended ? " ended" : ""}`} aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            {ended ? <path d="M12 21s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12zM9 9l6 6M15 9l-6 6" /> : <path d="M12 7v6M12 17h.01M12 3l9 16H3z" />}
          </svg>
        </span>
        <h1>{title}</h1>
        <p>{body}</p>
        <a className="btn-emergency" href="tel:190">Emergência 190</a>
      </section>
    </Shell>
  );
}

const initials = (name: string) => name.split(/\s+/).filter(Boolean).map((w) => w[0]!.toUpperCase()).slice(0, 2).join("");
